/**
 * Simple Router - ES5 compatible router for older browsers (Chrome 70+, Samsung TV)
 * Replaces react-router-dom with basic hash-based routing
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Router Context
var RouterContext = createContext({
  path: '/',
  params: {},
  query: {},
  navigate: function() {},
  location: { pathname: '/', search: '', hash: '' }
});

/**
 * Parse URL path and extract params based on route pattern
 * e.g., pattern: "/display/:qrCodeId", path: "/display/abc123" -> { qrCodeId: "abc123" }
 */
function matchRoute(pattern, path) {
  var patternParts = pattern.split('/').filter(Boolean);
  var pathParts = path.split('/').filter(Boolean);
  
  if (patternParts.length !== pathParts.length) {
    return null;
  }
  
  var params = {};
  
  for (var i = 0; i < patternParts.length; i++) {
    var patternPart = patternParts[i];
    var pathPart = pathParts[i];
    
    if (patternPart.startsWith(':')) {
      // This is a parameter
      var paramName = patternPart.substring(1);
      params[paramName] = decodeURIComponent(pathPart);
    } else if (patternPart !== pathPart) {
      // Static part doesn't match
      return null;
    }
  }
  
  return params;
}

/**
 * Parse query string into object
 */
function parseQuery(search) {
  var query = {};
  if (!search || search.length <= 1) {
    return query;
  }
  
  var queryString = search.startsWith('?') ? search.substring(1) : search;
  var pairs = queryString.split('&');
  
  for (var i = 0; i < pairs.length; i++) {
    var pair = pairs[i].split('=');
    if (pair[0]) {
      query[decodeURIComponent(pair[0])] = pair[1] ? decodeURIComponent(pair[1]) : '';
    }
  }
  
  return query;
}

/**
 * Get current path from URL (supports both hash and regular routing)
 */
function getCurrentPath() {
  // Use pathname for regular routing
  return window.location.pathname || '/';
}

/**
 * SimpleRouter - Main router provider component
 */
export function SimpleRouter(props) {
  var children = props.children;
  
  var getLocation = useCallback(function() {
    return {
      pathname: window.location.pathname || '/',
      search: window.location.search || '',
      hash: window.location.hash || ''
    };
  }, []);
  
  var _state = useState(getLocation());
  var location = _state[0];
  var setLocation = _state[1];
  
  var navigate = useCallback(function(to, options) {
    var opts = options || {};
    if (opts.replace) {
      window.history.replaceState(null, '', to);
    } else {
      window.history.pushState(null, '', to);
    }
    setLocation(getLocation());
  }, [getLocation]);
  
  useEffect(function() {
    var handlePopState = function() {
      setLocation(getLocation());
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return function() {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getLocation]);
  
  var contextValue = {
    path: location.pathname,
    params: {},
    query: parseQuery(location.search),
    navigate: navigate,
    location: location
  };
  
  return React.createElement(RouterContext.Provider, { value: contextValue }, children);
}

/**
 * Routes - Container for Route components
 */
export function Routes(props) {
  var children = props.children;
  var context = useContext(RouterContext);
  var currentPath = context.path;
  
  // Convert children to array
  var childArray = React.Children.toArray(children);
  
  // Find first matching route
  for (var i = 0; i < childArray.length; i++) {
    var child = childArray[i];
    if (child && child.props && child.props.path !== undefined) {
      var pattern = child.props.path;
      var element = child.props.element;
      
      // Check for exact match or pattern match
      var params = null;
      
      if (pattern === currentPath) {
        params = {};
      } else {
        params = matchRoute(pattern, currentPath);
      }
      
      if (params !== null) {
        // Clone element with params
        return React.createElement(
          RouterContext.Provider,
          { 
            value: Object.assign({}, context, { params: params })
          },
          element
        );
      }
    }
  }
  
  // No match found - return null or could return a 404 component
  return null;
}

/**
 * Route - Individual route definition (just a marker, rendering handled by Routes)
 */
export function Route(props) {
  // Route doesn't render anything directly, it's processed by Routes
  return null;
}

/**
 * Link - Navigation link component
 */
export function Link(props) {
  var to = props.to;
  var children = props.children;
  var className = props.className;
  var style = props.style;
  var onClick = props.onClick;
  
  var context = useContext(RouterContext);
  
  var handleClick = function(e) {
    // Allow cmd/ctrl click to open in new tab
    if (e.metaKey || e.ctrlKey) {
      return;
    }
    
    e.preventDefault();
    
    if (onClick) {
      onClick(e);
    }
    
    context.navigate(to);
  };
  
  return React.createElement('a', {
    href: to,
    className: className,
    style: style,
    onClick: handleClick
  }, children);
}

/**
 * useNavigate - Hook to get navigate function
 */
export function useNavigate() {
  var context = useContext(RouterContext);
  return context.navigate;
}

/**
 * useParams - Hook to get route parameters
 */
export function useParams() {
  var context = useContext(RouterContext);
  return context.params;
}

/**
 * useLocation - Hook to get current location
 */
export function useLocation() {
  var context = useContext(RouterContext);
  return context.location;
}

/**
 * useSearchParams - Hook to get and set query parameters
 */
export function useSearchParams() {
  var context = useContext(RouterContext);
  var query = context.query;
  
  var searchParams = {
    get: function(key) {
      return query[key] || null;
    },
    getAll: function(key) {
      var value = query[key];
      return value ? [value] : [];
    },
    has: function(key) {
      return key in query;
    },
    toString: function() {
      var parts = [];
      for (var key in query) {
        if (query.hasOwnProperty(key)) {
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(query[key]));
        }
      }
      return parts.join('&');
    }
  };
  
  var setSearchParams = function(newParams) {
    var queryString = '';
    var parts = [];
    
    if (typeof newParams === 'function') {
      newParams = newParams(searchParams);
    }
    
    if (newParams && typeof newParams.toString === 'function') {
      queryString = newParams.toString();
    } else if (typeof newParams === 'object') {
      for (var key in newParams) {
        if (newParams.hasOwnProperty(key)) {
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(newParams[key]));
        }
      }
      queryString = parts.join('&');
    }
    
    var newUrl = context.location.pathname + (queryString ? '?' + queryString : '');
    context.navigate(newUrl, { replace: true });
  };
  
  return [searchParams, setSearchParams];
}

// Also export as BrowserRouter for compatibility
export var BrowserRouter = SimpleRouter;

// Default export
export default {
  SimpleRouter: SimpleRouter,
  BrowserRouter: SimpleRouter,
  Routes: Routes,
  Route: Route,
  Link: Link,
  useNavigate: useNavigate,
  useParams: useParams,
  useLocation: useLocation,
  useSearchParams: useSearchParams
};
